import React from 'react'

import { Loader2 } from 'lucide-react'

import { cn } from '@/utils/cn'

interface PageContainerProps {
  /**
   * Contenido principal de la página
   */
  children: React.ReactNode

  /**
   * Título de la página (se muestra en el header del container)
   */
  title?: string

  /**
   * Descripción opcional debajo del título
   */
  description?: string

  /**
   * Acciones que se muestran en el header (botones, etc.)
   */
  actions?: React.ReactNode

  /**
   * Estado de carga
   * @default false
   */
  loading?: boolean

  /**
   * Mensaje personalizado durante la carga
   */
  loadingMessage?: string

  /**
   * Si el contenedor debe tener ancho máximo
   * @default true
   */
  maxWidth?: boolean

  /**
   * Tamaño del ancho máximo
   * @default 'default'
   */
  size?: 'sm' | 'default' | 'lg' | 'xl' | 'full'

  /**
   * Padding del contenedor
   * @default 'default'
   */
  padding?: 'none' | 'sm' | 'default' | 'lg'

  /**
   * Clases CSS adicionales
   */
  className?: string

  /**
   * Si debe mostrar un fondo diferente
   * @default 'default'
   */
  variant?: 'default' | 'gray' | 'white'
}

const sizeClasses = {
  sm: 'max-w-4xl',
  default: 'max-w-6xl',
  lg: 'max-w-7xl',
  xl: 'max-w-[1400px]',
  full: 'max-w-full'
}

const paddingClasses = {
  none: '',
  sm: 'px-4 py-4 sm:px-6 sm:py-6',
  default: 'px-4 py-6 sm:px-6 sm:py-8',
  lg: 'px-4 py-8 sm:px-6 sm:py-10 lg:px-8'
}

const variantClasses = {
  default: '',
  gray: 'bg-gray-50 min-h-full',
  white: 'bg-white shadow-sm rounded-lg'
}

export const PageContainer: React.FC<PageContainerProps> = ({
  children,
  title,
  description,
  actions,
  loading = false,
  loadingMessage = 'Cargando...',
  maxWidth = true,
  size = 'default',
  padding = 'default',
  className,
  variant = 'default'
}) => {
  // Si está cargando, mostrar spinner centrado
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">{loadingMessage}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex-1', variantClasses[variant])}>
      <div
        className={cn(
          'w-full',
          maxWidth && sizeClasses[size],
          maxWidth && 'mx-auto',
          paddingClasses[padding],
          className
        )}
      >
        {/* Header con título y acciones */}
        {(title || actions) && (
          <div className="mb-6">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                {title && <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{title}</h1>}
                {description && (
                  <p className="mt-2 text-sm text-gray-600 sm:text-base">{description}</p>
                )}
              </div>
              {actions && <div className="mt-4 flex flex-shrink-0 md:mt-0 md:ml-4">{actions}</div>}
            </div>
          </div>
        )}

        {/* Contenido principal */}
        <div className="page-content">{children}</div>
      </div>
    </div>
  )
}
